import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Check, Crown, Zap, Clock, CreditCard, Users, Star, ArrowLeft } from "lucide-react";
import { useAuth } from '../hooks/use-auth';
import { Link, useLocation } from "wouter";

type PlanItem = {
  name: string;
  price: number;
  description: string;
  tagline?: string;
  features: string[];
  limitations: string[];
  icon: typeof Users;
  color: string;
  popular: boolean;
};

const PricingPage = () => {
  const renderFeatureText = (feature: string) => {
    if (feature.includes("$8 per report after")) {
      const [prefix] = feature.split("· $8 per report after");
      return (
        <>
          {prefix.trim()} · <span className="line-through text-slate-500">$8 per report after</span> ·{" "}
          <span className="text-green-700 font-semibold">now completely free</span>
        </>
      );
    }
    return feature;
  };

  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const plans: PlanItem[] = [
    {
      name: "Basic",
      price: 49,
      description: "Look professional and get organized — win trust from day one",
      tagline: "Every dollar goes toward looking credible.",
      features: [
        "1 user included · add team at +$11/user/month",
        "Clients, estimates & invoices",
        "Project management & client portal",
        "Email support"
      ],
      limitations: [
        "No AI features (upgrade to Pro for AI estimates & roof reports)",
        "No time clock"
      ],
      icon: Users,
      color: "green",
      popular: false
    },
    {
      name: "Pro",
      price: 79,
      description: "AI that writes estimates and roof reports — close more deals, faster",
      tagline: "Win more bids without working more hours.",
      features: [
        "1 user included · add team at +$11/user/month",
        "5 AI roof reports/month included · $8 per report after",
        "AI estimate descriptions & AI project tasks",
        "Calendar, time clock, priority support",
        "All Basic features"
      ],
      limitations: [
        "No team/agents (see Business)"
      ],
      icon: Zap,
      color: "green",
      popular: true
    },
    {
      name: "Business",
      price: 149,
      description: "Full team features and control as you grow",
      tagline: "Scale your crew and your revenue in one place.",
      features: [
        "1 user included · add team at +$11/user/month",
        "5 AI roof reports/month included · $8 per report after",
        "Advanced client portal experience for bigger teams",
        "Team/agents management & full timeclock",
        "Premium support · All Pro features"
      ],
      limitations: [],
      icon: Crown,
      color: "green",
      popular: false
    }
  ];

  const handleSubscribe = (planName: string) => {
    const planKey = planName.toLowerCase();
    // "Start Free Trial" always goes to register with the chosen plan
    setLocation(`/register?plan=${planKey}`);
  };

  return (
    <div className="light min-h-screen bg-white">
      <Helmet>
        <title>Pricing – Remodra | Contractor Software Plans</title>
        <meta name="description" content="Remodra plans: Basic, Pro, and Business. Construction management, estimates, invoices, AI roof reports. Start free trial." />
        <link rel="canonical" href="https://getremodra.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://getremodra.com/pricing" />
        <meta property="og:title" content="Pricing – Remodra | Contractor Software Plans" />
        <meta property="og:description" content="Remodra plans: Basic, Pro, and Business. Construction management, estimates, invoices, AI roof reports. Start free trial." />
        <meta property="og:image" content="https://getremodra.com/remodra-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://getremodra.com/pricing" />
        <meta name="twitter:title" content="Pricing – Remodra | Contractor Software Plans" />
        <meta name="twitter:description" content="Remodra plans: Basic, Pro, and Business. Construction management, estimates, invoices, AI roof reports. Start free trial." />
        <meta name="twitter:image" content="https://getremodra.com/remodra-logo.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://getremodra.com/" },
              { "@type": "ListItem", position: 2, name: "Pricing" }
            ]
          })}
        </script>
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header Card */}
          <Card className="!bg-white !border-slate-300 !text-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-slate-800">
                    <Crown className="h-6 w-6 text-green-500" />
                    Plans That Help You Make More
                  </CardTitle>
                  <p className="text-slate-600 mt-2">
                    Every plan is built to save you time and win more jobs. Start free, scale when you're ready.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setLocation('/')}
                  className="text-slate-600 hover:text-green-500"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 md:items-stretch">
            {plans.map((plan) => {
              const IconComponent = plan.icon;
              return (
                <Card 
                  key={plan.name} 
                  className={`flex flex-col h-full relative overflow-hidden !bg-white !border-slate-300 ${
                    plan.popular 
                      ? 'border-2 !border-green-500 shadow-lg' 
                      : 'hover:shadow-md transition-shadow'
                  }`}
                >
                {plan.popular && (
                  <div className="bg-green-500 text-white text-center py-2 text-sm font-semibold rounded-t-lg">
                    <Star className="inline h-4 w-4 mr-1" />
                    Most Popular · Win More Bids
                  </div>
                )}
                
                <CardHeader className={`text-center ${plan.popular ? 'pt-6' : 'pt-8'}`}>
                  <div className="mx-auto mb-4 p-3 rounded-full bg-green-100">
                    <IconComponent className="h-8 w-8 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-800">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-800">
                      ${plan.price}
                    </span>
                    <span className="text-slate-600">/month</span>
                  </div>
                  <p className="text-slate-600 mt-2 font-medium">
                    {plan.description}
                  </p>
                  {plan.tagline && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      {plan.tagline}
                    </p>
                  )}
                </CardHeader>

                <CardContent className="flex flex-col flex-1 min-h-0 pt-6 space-y-6">
                  <div className="space-y-6 flex-1">
                    {/* Features */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-green-600 dark:text-green-400">
                        Included Features:
                      </h4>
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{renderFeatureText(feature)}</span>
                        </div>
                      ))}
                    </div>

                    {/* Limitations */}
                    {plan.limitations.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-600">
                          Not Included:
                        </h4>
                        {plan.limitations.map((limitation, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="h-4 w-4 rounded-full bg-slate-400 flex-shrink-0" />
                            <span className="text-sm text-slate-600">
                              {limitation}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-4 mt-auto">
                    <Button 
                      onClick={() => handleSubscribe(plan.name)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      size="lg"
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

          {/* Additional Info */}
          <Card className="!bg-white !border-slate-300 !text-slate-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  No Risk, No Surprises
                </h2>
                <p className="text-slate-600 mb-6">Invest in tools that pay for themselves.</p>
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 rounded-full bg-green-100 w-16 h-16 flex items-center justify-center">
                      <Clock className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">7-Day Free Trial</h3>
                    <p className="text-slate-600">Try any plan risk-free — no credit card required to start</p>
                  </div>
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 rounded-full bg-green-100 w-16 h-16 flex items-center justify-center">
                      <CreditCard className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Cancel Anytime</h3>
                    <p className="text-slate-600">No long-term contracts or hidden fees</p>
                  </div>
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 rounded-full bg-green-100 w-16 h-16 flex items-center justify-center">
                      <Users className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Support When You Need It</h3>
                    <p className="text-slate-600">Get help so you can get back to winning jobs</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <Card className="!bg-white !border-slate-300 !text-slate-800">
            <CardContent className="pt-6 text-center">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">
                Ready to Win More Jobs and Get Paid Faster?
              </h2>
              <p className="text-xl text-slate-600 mb-8">
                Join contractors who use Remodra to look professional, save time, and close more deals
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setLocation('/register')}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3"
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setLocation('/simple-login')}
                  className="text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-lg px-8 py-3"
                >
                  Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;